import { motion } from "framer-motion";
import { Sparkles, Wind, Smartphone } from "lucide-react";
import { useLanguage } from "@/contexts/language-context";

export function InteriorShowcase() {
  const { t } = useLanguage();

  return (
    <section className="py-24 bg-white">
      <div className="container mx-auto px-4">
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-block mb-4 px-6 py-2 bg-blue-100 rounded-full">
            <span className="text-blue-600 font-semibold">{t("interior.badge")}</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            {t("interior.title")}
            <span className="block text-blue-600">{t("interior.titleHighlight")}</span>
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            {t("interior.subtitle")}
          </p>
        </motion.div>

        <div className="max-w-7xl mx-auto">
          {/* Main Interior Showcase */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16"
          >
            {/* Luxury Interior */}
            <div className="relative group">
              <div className="relative aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl">
                <img src="/img/内饰1排-2.webp"
                  alt={t("interior.luxury.title")}
                  className="object-cover group-hover:scale-110 transition-transform duration-700"
                  sizes="(max-width: 1024px) 100vw, 50vw"
                  style={{width: "100%", height: "100%", objectFit: "cover"}} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <div className="absolute bottom-8 left-8 text-white">
                  <h3 className="text-3xl font-bold mb-2">{t("interior.luxury.title")}</h3>
                  <p className="text-blue-200">{t("interior.luxury.desc")}</p>
                </div>
              </div>
            </div>

            {/* Commercial Interior */}
            <div className="relative group">
              <div className="relative aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl">
                <img src="/img/内饰1排-1.webp"
                  alt={t("interior.commercial.title")}
                  className="object-cover group-hover:scale-110 transition-transform duration-700"
                  sizes="(max-width: 1024px) 100vw, 50vw"
                  style={{width: "100%", height: "100%", objectFit: "cover"}} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <div className="absolute bottom-8 left-8 text-white">
                  <h3 className="text-3xl font-bold mb-2">{t("interior.commercial.title")}</h3>
                  <p className="text-blue-200">{t("interior.commercial.desc")}</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Interior Features */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {[
              {
                icon: Sparkles,
                title: t("interior.instrument.title"),
                description: t("interior.instrument.description"),
                image: "/img/数字仪表盘.png",
              },
              {
                icon: Smartphone,
                title: t("interior.control.title"),
                description: t("interior.control.description"),
                image: "/img/怀挡.png",
              },
              {
                icon: Wind,
                title: t("interior.ac.title"),
                description: t("interior.ac.description"),
                image: "/img/后排冷气照片.webp",
              },
            ].map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  className="group"
                >
                  <div className="relative aspect-video rounded-2xl overflow-hidden mb-6 shadow-lg">
                    <img src={feature.image}
                      alt={feature.title}
                      className="object-cover group-hover:scale-110 transition-transform duration-500"
                      sizes="(max-width: 768px) 100vw, 33vw"
                      style={{width: "100%", height: "100%", objectFit: "cover"}} />
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <Icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                      <p className="text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

      
        </div>
      </div>
    </section>
  );
}

